import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  AuthWrapper,
  FormWrapper,
  Content,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";

import TextInput from "../../components/inputs/TextInput";
import PasswordInput from "../../components/inputs/PasswordInput";
import Button from "../../components/button/index";
import ButtonLink from "../../components/link/ButtonLink";

import { acceptInvitation, previewInvitation } from "../../store/user/userService";
import { vocabularyForKind } from "../team-admin/vocabulary";

const STATUS = {
  LOADING: "loading",
  READY: "ready",
  INVALID: "invalid",
  ERROR: "error",
};

// Where to go once the invitation is accepted. Club invitations carry no
// player (plan decision 13): the player picks his own profile first.
export const pathAfterAccept = (result) =>
  result?.onboarding?.needs_player_pick
    ? "/player/onboarding/pick-player"
    : "/player/dashboard";

const hasSessionToken = () => {
  try {
    const token = localStorage.getItem("token");
    return Boolean(token && token !== "undefined");
  } catch (e) {
    return false;
  }
};

const AcceptInvitation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState(STATUS.LOADING);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");
  // Existing account: try the signed-in (no password) path first, then offer
  // "sign in" / "set a new password" (plan §5.6 step 5).
  const [silentAccepting, setSilentAccepting] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const playerDisplayName = useMemo(() => preview?.player?.display_name || "", [preview]);
  const organizationName = useMemo(() => preview?.organization?.name || "", [preview]);
  const organizationKind = preview?.organization?.kind || "agency";
  const vocabulary = useMemo(
    () => vocabularyForKind(organizationKind, organizationName),
    [organizationKind, organizationName]
  );
  const accountExists = Boolean(preview?.account_exists);

  const finish = (result) => {
    navigate(pathAfterAccept(result), { replace: true });
  };

  const describeAcceptError = (err) => {
    switch (err?.code) {
      case "ALREADY_IN_ANOTHER_ORGANIZATION":
        return "This account is still registered with another club or organization. Ask them to remove you first, then open this link again.";
      case "USER_ALREADY_MAPPED":
        return "This account is already linked to a different player profile. Contact support@smrtstats.com to switch.";
      case "ORGANIZATION_SUSPENDED":
        return `${organizationName || "This organization"}'s SmrtStats access is currently switched off. Ask them before accepting.`;
      default:
        return err?.message || "Failed to accept invitation.";
    }
  };

  useEffect(() => {
    if (!token) {
      setStatus(STATUS.INVALID);
      setLoadError("No invitation token provided.");
      return undefined;
    }
    let active = true;
    dispatch(previewInvitation(token))
      .unwrap()
      .then(async (data) => {
        if (!active) return;
        setPreview(data);

        // Already signed in as the invited account → accept without a form.
        // If the session belongs to someone else the API answers 422 and we
        // simply fall through to the choices below.
        if (data?.account_exists && hasSessionToken()) {
          setSilentAccepting(true);
          try {
            const result = await dispatch(acceptInvitation({ token })).unwrap();
            if (!active) return;
            finish(result);
            return;
          } catch (err) {
            if (!active) return;
            if (err?.code === "INVITATION_INVALID") {
              setStatus(STATUS.INVALID);
              setLoadError(err.message);
              return;
            }
            if (err?.code && err.code !== "VALIDATION" && err.status !== 422) {
              setSubmitError(describeAcceptError(err));
            }
          } finally {
            if (active) setSilentAccepting(false);
          }
        }

        setStatus(STATUS.READY);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.code === "INVITATION_INVALID") {
          setStatus(STATUS.INVALID);
          setLoadError(err.message || "This invitation link is invalid, expired, or already used.");
        } else {
          setStatus(STATUS.ERROR);
          setLoadError(err?.message || "Failed to load invitation.");
        }
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch]);

  const validate = () => {
    if (!firstName.trim()) return "Please enter your first name.";
    if (!lastName.trim()) return "Please enter your last name.";
    if (password.length < 8) return "Password must be at least 8 characters long.";
    if (password !== confirmPassword) return "Passwords do not match.";
    return "";
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const validation = validate();
    if (validation) {
      setSubmitError(validation);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const result = await dispatch(acceptInvitation({
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      })).unwrap();
      finish(result);
    } catch (err) {
      if (err?.code === "INVITATION_INVALID") {
        setStatus(STATUS.INVALID);
        setLoadError(err.message);
      } else {
        setSubmitError(describeAcceptError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const signInToAccept = () => {
    const next = `${location.pathname}${location.search}`;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  };

  const showForm = status === STATUS.READY && (!accountExists || showPasswordForm);
  const showExistingChoices = status === STATUS.READY && accountExists && !showPasswordForm;

  return (
    <AuthWrapper>
      <FormWrapper>
        <Content>
          <Form onSubmit={handleSubmit}>
            <img className="logo" src={logo} alt="Logo" />

            {(status === STATUS.LOADING || silentAccepting) && (
              <>
                <h1>{silentAccepting ? "Accepting invitation..." : "Loading invitation..."}</h1>
                <p>{silentAccepting ? "Linking the invitation to your account." : "Please wait while we verify your invitation."}</p>
              </>
            )}

            {status === STATUS.INVALID && (
              <>
                <h1>Invitation unavailable</h1>
                <p>{loadError}</p>
                <LinkWrap>
                  <h3>
                    <ButtonLink
                      title="Back to login"
                      size="large"
                      color="primary"
                      underline
                      href="/login"
                    />
                  </h3>
                </LinkWrap>
              </>
            )}

            {status === STATUS.ERROR && (
              <>
                <h1>Something went wrong</h1>
                <ErrorMessage>{loadError}</ErrorMessage>
                <LinkWrap>
                  <h3>
                    <ButtonLink
                      title="Back to login"
                      size="large"
                      color="primary"
                      underline
                      href="/login"
                    />
                  </h3>
                </LinkWrap>
              </>
            )}

            {status === STATUS.READY && !silentAccepting && (
              <>
                <h1>Accept your invitation</h1>
                <p>{vocabulary.acceptIntro(organizationName)}</p>

                {(playerDisplayName || preview?.email) && (
                  <InvitationSummary
                    email={preview?.email}
                    playerName={playerDisplayName}
                    organizationName={organizationName}
                    organizationLabel={vocabulary.acceptOrgLabel}
                    needsPlayerPick={Boolean(preview?.needs_player_pick)}
                  />
                )}

                {showExistingChoices && (
                  <>
                    <p>
                      An account with <strong>{preview?.email}</strong> already exists.
                      Sign in to add this invitation to it, or set a new password.
                    </p>
                    {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
                    <ConfirmWrapp>
                      <Button
                        title="Sign in to accept"
                        size="full"
                        sizeResponse="full"
                        type="button"
                        onClick={signInToAccept}
                      />
                    </ConfirmWrapp>
                    <LinkWrap>
                      <h3>
                        <button
                          type="button"
                          onClick={() => { setSubmitError(""); setShowPasswordForm(true); }}
                          style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit", padding: 0 }}
                        >
                          Set a new password instead
                        </button>
                      </h3>
                    </LinkWrap>
                  </>
                )}

                {showForm && (
                  <>
                    {accountExists && (
                      <p style={{ fontSize: 13, opacity: 0.8 }}>
                        This updates the password of your existing SmrtStats account.
                      </p>
                    )}
                    <TextInput
                      label="First name"
                      type="text"
                      value={firstName}
                      placeholder="Enter your first name"
                      onChange={setFirstName}
                    />
                    <TextInput
                      label="Last name"
                      type="text"
                      value={lastName}
                      placeholder="Enter your last name"
                      onChange={setLastName}
                    />
                    <PasswordInput
                      label="Password"
                      placeholder="Choose a password (8+ characters)"
                      value={password}
                      onChange={setPassword}
                      bottomText="Must be at least 8 characters."
                    />
                    <PasswordInput
                      label="Confirm password"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                    />

                    {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

                    <ConfirmWrapp>
                      <Button
                        title={submitting ? "Creating account..." : "Accept invitation"}
                        size="full"
                        sizeResponse="full"
                        type="submit"
                        disabled={submitting}
                      />
                    </ConfirmWrapp>
                  </>
                )}
              </>
            )}
          </Form>
        </Content>
      </FormWrapper>
      <ImageWrapper></ImageWrapper>
    </AuthWrapper>
  );
};

const InvitationSummary = ({ email, playerName, organizationName, organizationLabel, needsPlayerPick }) => (
  <div style={{
    background: 'rgba(200,255,0,0.08)',
    border: '1px solid rgba(200,255,0,0.25)',
    borderRadius: 8,
    padding: '12px 16px',
    margin: '12px 0 20px',
    fontSize: 14,
    lineHeight: 1.6,
  }}>
    {playerName && (
      <div><strong>Player profile:</strong> {playerName}</div>
    )}
    {!playerName && needsPlayerPick && (
      <div><strong>Player profile:</strong> you pick it after accepting</div>
    )}
    {organizationName && (
      <div><strong>{organizationLabel || 'Organization'}:</strong> {organizationName}</div>
    )}
    {email && (
      <div><strong>Email:</strong> {email}</div>
    )}
  </div>
);

export default AcceptInvitation;
